// AR device-capability classification (ADR-0035 §6/§7, issue #382). Decides — on
// the CLIENT, before any tap — which AR affordance a device can offer, so the
// "Zobacz w swoim pokoju" entry point is "never shown broken":
//
//   • native      → the device can open surface-locked OS AR here (iOS AR Quick
//                    Look, or Android WebXR `immersive-ar` / Scene Viewer). #382
//                    wires THIS branch.
//   • qr-handoff  → a desktop with no local AR path, but it can still bake + park
//                    the asset and hand off to a phone via a QR code (#383).
//   • unavailable → a mobile browser with no AR path at all → the button hides.
//
// The classifier is a PURE function over an injected `ArCapabilityEnv` (UA + touch
// + resolved feature-probes) — it reads no globals — so the whole iOS / Android /
// desktop / unsupported matrix unit-tests without a DOM. The browser adapter
// (`probeArCapability`) gathers the real globals + async probes and delegates to it.

export type ArCapability = "native" | "qr-handoff" | "unavailable";

// Everything the pure classifier needs, all injected (never read from globals
// inside the core). The three booleans are resolved feature-probes the adapter
// computes from the live browser.
export interface ArCapabilityEnv {
  // `navigator.userAgent`.
  userAgent: string;
  // `navigator.maxTouchPoints` — disambiguates iPadOS (which reports a desktop
  // "Macintosh" UA) from a real Mac.
  maxTouchPoints: number;
  // `<a rel="ar">` is supported (`relList.supports("ar")`) — iOS Safari's signal
  // that AR Quick Look can open a USDZ.
  quickLookSupported: boolean;
  // `navigator.xr.isSessionSupported("immersive-ar")` resolved to a boolean —
  // Android WebXR AR.
  webXrArSupported: boolean;
  // The browser can launch the Android Scene Viewer `intent:` URL (the GLB AR
  // fallback when WebXR is absent). The adapter derives this from the UA.
  sceneViewerSupported: boolean;
}

// iPhone/iPod always report their model in the UA; iPad may too, OR (iPadOS 13+
// Safari) masquerade as a desktop "Macintosh" while exposing touch — so a
// Macintosh UA with multi-touch is treated as an iPad.
export function isIosDevice(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return true;
  return /Macintosh/i.test(userAgent) && maxTouchPoints > 1;
}

export function isAndroidDevice(userAgent: string): boolean {
  return /Android/i.test(userAgent);
}

// A mobile UA that is NEITHER iOS nor Android (the standard "Mobi" token). Used
// only to keep the residual (non-iOS/Android) case honest: a phone with no AR path
// hides, and never gets the desktop-only QR handoff.
function isOtherMobileDevice(userAgent: string): boolean {
  return /Mobi/i.test(userAgent);
}

export function arCapability(env: ArCapabilityEnv): ArCapability {
  if (isIosDevice(env.userAgent, env.maxTouchPoints)) {
    // iOS has no WebXR — AR Quick Look (USDZ) is the only path.
    return env.quickLookSupported ? "native" : "unavailable";
  }
  if (isAndroidDevice(env.userAgent)) {
    // WebXR immersive-ar OR the Scene Viewer intent fallback.
    return env.webXrArSupported || env.sceneViewerSupported ? "native" : "unavailable";
  }
  // Neither iOS nor Android. A desktop can't run local AR but CAN bake + park +
  // hand off to a phone (QR, #383); a residual mobile with no AR path just hides —
  // a QR to scan from the same phone is nonsensical.
  return isOtherMobileDevice(env.userAgent) ? "unavailable" : "qr-handoff";
}

// ── Browser adapter (impure) ─────────────────────────────────────────────────
// Gathers the live globals + async feature-probes, then delegates to the pure
// `arCapability`. Kept thin (no branching logic of its own) so the decision stays
// in the tested core.

// iOS Safari signals AR Quick Look support via `<a rel="ar">` in the anchor's
// relList — the same probe `<model-viewer>` uses. `DOMTokenList.supports()` THROWS a
// TypeError where the attribute defines no supported-token set (per spec, and in
// jsdom), so it is guarded — a throw means "no Quick Look" here.
function probeQuickLook(): boolean {
  if (typeof document === "undefined") return false;
  try {
    const anchor = document.createElement("a");
    return Boolean(anchor.relList?.supports?.("ar"));
  } catch {
    return false;
  }
}

interface NavigatorXR {
  xr?: { isSessionSupported?: (mode: string) => Promise<boolean> };
}

// `navigator.xr.isSessionSupported("immersive-ar")`, guarded so a missing WebXR or
// a rejecting probe resolves to `false` rather than throwing.
async function probeWebXrImmersiveAr(): Promise<boolean> {
  if (typeof navigator === "undefined") return false;
  const xr = (navigator as Navigator & NavigatorXR).xr;
  if (!xr?.isSessionSupported) return false;
  try {
    return await xr.isSessionSupported("immersive-ar");
  } catch {
    return false;
  }
}

// Scene Viewer launches from an `intent:` URL that needs a browser able to fire it
// — Chrome, Samsung Internet, or an Android WebView. Firefox for Android can't (and
// has no WebXR), so it correctly falls through to `unavailable`.
function isSceneViewerCandidate(userAgent: string): boolean {
  return isAndroidDevice(userAgent) && /Chrome|SamsungBrowser|; wv\)/i.test(userAgent);
}

// The single browser entry point: probe the device and classify it. Async because
// the WebXR probe is async; SSR (no navigator) resolves to `unavailable` and the
// client re-probes after hydration.
export async function probeArCapability(): Promise<ArCapability> {
  if (typeof navigator === "undefined") return "unavailable";
  const userAgent = navigator.userAgent;
  return arCapability({
    userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    quickLookSupported: probeQuickLook(),
    webXrArSupported: await probeWebXrImmersiveAr(),
    sceneViewerSupported: isSceneViewerCandidate(userAgent),
  });
}
